// Phase 8.8 — Affiliate Commission Dashboard
// Surfaces the full breakdown from /api/payouts/me/dashboard plus links
// to the payout-destination settings page.

import React, { useEffect, useState } from "react";
import { DollarSign, ArrowRight, CheckCircle2, Clock, XCircle, Wallet } from "lucide-react";
import { Link } from "react-router-dom";
import api, { apiErrorMessage } from "@/lib/api";

const SOURCE_LABEL = {
  storefront:         "Storefront sales",
  affiliate_program:  "Affiliate program",
  same_day_delivery:  "Same-day delivery",
  partner_residual:   "Partner residuals",
  franchise_residual: "Franchise residuals",
};

const STATUS_TONE = {
  pending_review: { icon: Clock,        cls: "text-amber-300 bg-amber-400/15 border-amber-400/30" },
  approved:       { icon: CheckCircle2, cls: "text-sky-300 bg-sky-500/15 border-sky-500/30" },
  paid:           { icon: CheckCircle2, cls: "text-lifted-green bg-lifted-green/15 border-lifted-green/30" },
  void:           { icon: XCircle,      cls: "text-rose-300 bg-rose-500/15 border-rose-500/30" },
};

function dollars(cents) {
  if (cents == null) return "—";
  return `$${(cents / 100).toFixed(2)}`;
}

export default function AffiliateCommissions() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/payouts/me/dashboard");
        setData(data);
      } catch (e) { setErr(apiErrorMessage(e)); }
    })();
  }, []);

  if (err) return <div className="max-w-6xl mx-auto px-4 py-10 text-rose-300" data-testid="commissions-error">{err}</div>;
  if (!data) return <div className="max-w-6xl mx-auto px-4 py-10 text-sm text-lifted-dim">Loading…</div>;
  if (!data.profile) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-10" data-testid="commissions-no-profile">
        <div className="glass-panel p-6 text-center">
          <h1 className="font-anton text-2xl uppercase mb-2">No affiliate profile</h1>
          <p className="text-sm text-lifted-dim">Activate your affiliate role to start earning commissions.</p>
        </div>
      </div>
    );
  }

  const totals = data.totals || {};

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 space-y-6" data-testid="commissions-dashboard">
      <header className="flex flex-wrap gap-3 items-end justify-between">
        <div>
          <div className="eyebrow">Affiliate · Earnings</div>
          <h1 className="font-anton text-3xl uppercase">Commissions</h1>
          <p className="text-lifted-dim text-sm mt-1">Live ledger from every paid order in the ecosystem.</p>
        </div>
        <Link to="/affiliate/payouts"
              className="btn-lifted !py-2 !px-4 text-xs inline-flex items-center gap-2"
              data-testid="commissions-payouts-link">
          <Wallet className="h-3.5 w-3.5" /> Payout settings <ArrowRight className="h-3 w-3" />
        </Link>
      </header>

      {/* Totals */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="commissions-totals">
        <Kpi label="Lifetime"     value={dollars(totals.total_cents)} sub={`Across all sources`} icon={DollarSign} testid="kpi-lifetime" />
        <Kpi label="Pending"      value={dollars(totals.pending_review || 0)} sub="Awaiting review" icon={Clock} testid="kpi-pending" />
        <Kpi label="Approved"     value={dollars(totals.approved || 0)} sub="Cleared, awaiting payout" icon={CheckCircle2} testid="kpi-approved" />
        <Kpi label="Paid out"     value={dollars(totals.paid || 0)} sub="Already in your wallet" icon={CheckCircle2} testid="kpi-paid" />
      </section>

      {/* By source */}
      <section className="grid lg:grid-cols-2 gap-4" data-testid="commissions-by-source">
        {["storefront", "affiliate_program", "same_day_delivery", "partner_residual", "franchise_residual"].map((src) => {
          const s = data.by_source?.[src];
          if (!s) {
            return (
              <div key={src} className="glass-panel p-5 opacity-50" data-testid={`source-${src}-empty`}>
                <div className="text-[0.65rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase">{SOURCE_LABEL[src]}</div>
                <div className="text-sm text-lifted-dim mt-2">No commissions yet.</div>
              </div>
            );
          }
          return (
            <div key={src} className="glass-panel p-5" data-testid={`source-${src}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="text-[0.65rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase">{SOURCE_LABEL[src]}</div>
                <div className="font-anton text-2xl text-lifted-green">{dollars(s.total_cents)}</div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {Object.entries(s.by_status || {}).map(([st, v]) => (
                  <div key={st} className="flex items-center justify-between bg-black/30 rounded-md px-2 py-1.5 border border-white/5">
                    <span className="capitalize text-lifted-dim">{st.replace("_", " ")}</span>
                    <span className="font-mono">{dollars(v.amount_cents)}</span>
                  </div>
                ))}
              </div>
              <div className="text-[0.65rem] text-lifted-dim mt-2">{s.count} entries</div>
            </div>
          );
        })}
      </section>

      {/* Recent ledger */}
      <section className="glass-panel overflow-hidden" data-testid="commissions-ledger">
        <div className="px-4 py-3 border-b border-white/5 text-[0.65rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase">
          Recent activity
        </div>
        <table className="w-full text-sm">
          <thead className="text-[0.6rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase">
            <tr className="border-b border-white/5">
              <th className="px-3 py-2 text-left">Date</th>
              <th className="px-3 py-2 text-left">Source</th>
              <th className="px-3 py-2 text-right">Order</th>
              <th className="px-3 py-2 text-right">Commission</th>
              <th className="px-3 py-2 text-left">Status</th>
            </tr>
          </thead>
          <tbody>
            {data.recent?.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-lifted-dim text-xs">No commissions yet — keep sharing your link.</td></tr>
            )}
            {data.recent?.map((row) => {
              const tone = STATUS_TONE[row.status] || STATUS_TONE.pending_review;
              const Icon = tone.icon;
              return (
                <tr key={row.id} className="border-b border-white/5" data-testid={`ledger-row-${row.id}`}>
                  <td className="px-3 py-2 text-[0.65rem] text-lifted-dim font-mono">{row.created_at?.slice(0, 10)}</td>
                  <td className="px-3 py-2 text-xs">{SOURCE_LABEL[row.order_source] || row.order_source}</td>
                  <td className="px-3 py-2 text-right text-xs font-mono text-lifted-dim">{dollars(row.order_amount_cents)}</td>
                  <td className="px-3 py-2 text-right text-lifted-green font-bebas">{dollars(row.commission_cents)}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center gap-1 text-[0.6rem] font-bebas uppercase tracking-[0.18em] border rounded-full px-2 py-0.5 ${tone.cls}`}>
                      <Icon className="h-3 w-3" /> {row.status?.replace("_", " ")}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function Kpi({ label, value, sub, icon: Icon, testid }) {
  return (
    <div className="glass-panel p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-[0.65rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase">
        <Icon className="h-3.5 w-3.5 text-lifted-green" /> {label}
      </div>
      <div className="font-anton text-3xl text-white mt-2">{value}</div>
      {sub && <div className="text-[0.65rem] text-lifted-dim mt-1">{sub}</div>}
    </div>
  );
}
